import React, { useState } from "react";
import axios from "axios";
import { BellRing, Check, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Notify me when restocked" form, rendered on the PDP when a variant
 * is out of stock. Captures (product_id, variant_id, email) into the
 * restock_notifications collection. Triggered emails fire when admin
 * bumps stock above 0.
 */
const RestockNotifyForm = ({ productId, variantId, productName, variantLabel }) => {
  const [opened, setOpened] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    const trimmed = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/restock/notify`, {
        product_id: productId,
        variant_id: variantId,
        email: trimmed,
      });
      setSuccess(data.message || "We'll email you when this is back in stock.");
      setEmail("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!opened) {
    return (
      <div data-testid="restock-cta" className="mt-5">
        <button
          type="button"
          onClick={() => setOpened(true)}
          data-testid="restock-open-button"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#8B2252] hover:text-[#6e1a3f] underline-offset-4 hover:underline"
        >
          <BellRing className="w-4 h-4" strokeWidth={1.8} />
          Notify me when restocked
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <div
        data-testid="restock-success"
        className="mt-5 flex items-start gap-3 rounded-2xl px-4 py-3.5 border border-sage-200/60 bg-sage-100/70"
      >
        <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-sage-500 text-white shrink-0">
          <Check className="w-3.5 h-3.5" strokeWidth={2.4} />
        </span>
        <div className="text-sm text-charcoal-600 leading-relaxed">
          <p className="font-medium">You're on the list.</p>
          <p className="text-charcoal-500">{success}</p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="restock-form"
      className="mt-5 rounded-2xl p-4 bg-cream-200/60 border border-charcoal-100"
    >
      <div className="flex items-start gap-2 mb-3">
        <BellRing className="w-4 h-4 text-[#8B2252] mt-0.5 shrink-0" strokeWidth={1.8} />
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium">
            Get notified when back in stock
          </p>
          <p className="text-xs text-charcoal-500 mt-0.5">
            {productName} · {variantLabel}
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@lab.edu"
          data-testid="restock-email-input"
          className="input flex-1"
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid="restock-submit-button"
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
        >
          {submitting ? "Saving…" : "Notify me"}
        </button>
      </div>
      {error && (
        <p data-testid="restock-error" className="mt-2 text-xs text-red-700 inline-flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" strokeWidth={2} /> {error}
        </p>
      )}
      <p className="mt-2 text-[11px] text-charcoal-400">
        We'll only email you once for this compound. For research use only.
      </p>
    </form>
  );
};

export default RestockNotifyForm;
